/* Shared UI primitives for the new-look site: logo, neural canvas, reveal,
   buttons, eyebrow chip, section wrappers. Ported from shared.jsx. */
import {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { LineIcon } from './icons';

/* ===== Responsive helper — true at/below the mobile breakpoint (820px). ===== */
export const MOBILE_BP = 820;
export const useIsMobile = (bp: number = MOBILE_BP): boolean => {
  const [mobile, setMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= bp : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(`(max-width: ${bp}px)`);
    const onChange = () => setMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [bp]);
  return mobile;
};

/* ===== Logo — authentic layered double-F monogram ===== */
interface FemtoIconProps {
  size?: number;
  mono?: string;
}
export const FemtoIcon = ({ size = 34, mono = '' }: FemtoIconProps) => {
  // cls-1 = upper F (green), cls-2 = lower F (blue). mono overrides both.
  const top = mono || '#09AA00';
  const bot = mono || '#00A0F7';
  return (
    <svg width={size} height={size} viewBox='0 0 500 500' style={{ display: 'block' }}>
      <path
        fill={top}
        d='M257.17,81.41c-13.99,0-27.46,5.1-37.93,14.34l-86.7,76.47-37.95,33.2c-12.46,10.9-19.58,26.64-19.58,43.18v62.05h53.04l41.67-36.73v-97.78h198.53l56.76-94.73h-167.83ZM146.96,287.89h-49.2v-39.29c0-10,4.3-19.47,11.81-26.04l37.39-32.73v98.06ZM355.35,153.39h-167.06l45.98-40.56c6.33-5.57,14.44-8.66,22.89-8.66h127.68l-29.49,49.22Z'
      />
      <path
        fill={bot}
        d='M257.17,215.91c-13.62,0-26.77,4.84-37.11,13.64l-50.35,44.37-41.67,36.72-36.38,32.06c-10.67,10.69-16.67,25.19-16.67,40.39v35.49h94.71v-107.94h126.58l56.76-94.73h-95.88ZM146.96,395.83h-49.2v-12.74c0-9.97,4.3-19.47,11.81-26.04l37.39-32.73v71.5ZM283.4,287.89h-95.1l45.98-40.57c6.33-5.57,14.44-8.66,22.88-8.66h55.7l-29.47,49.22Z'
      />
    </svg>
  );
};

interface LogoProps {
  dark?: boolean;
  size?: number;
  showWord?: boolean;
  tagline?: boolean;
  mono?: string;
}
export const Logo = ({
  dark = false,
  size = 34,
  showWord = true,
  tagline = false,
  mono = '',
}: LogoProps) => {
  const ink = dark ? '#fff' : 'var(--indigo)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: size * 0.34 }}>
      <FemtoIcon size={size * 1.02} mono={dark ? mono || '#fff' : mono} />
      {showWord && (
        <div style={{ lineHeight: 1 }}>
          <div
            className='fw-display'
            style={{ fontWeight: 800, fontSize: size * 0.66, color: ink, letterSpacing: '-.03em' }}
          >
            Femtoware
          </div>
          {tagline && (
            <div
              className='fw-body'
              style={{
                fontWeight: 600,
                fontSize: size * 0.24,
                color: dark ? 'rgba(255,255,255,.6)' : 'var(--blue)',
                letterSpacing: '.01em',
                marginTop: 3,
              }}
            >
              Faster. Smarter. Precise.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ===== Animated neural particle field ===== */
interface NeuralCanvasProps {
  density?: number;
  color?: string;
  accent?: string;
  opacity?: number;
  /** Drift animation. Off by default — a still, faint field keeps the site calm. */
  animate?: boolean;
}
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  a: boolean;
}
export const NeuralCanvas = ({
  density = 46,
  color = '0,160,247',
  accent = '47,209,31',
  opacity: rawOpacity = 1,
  animate = false,
}: NeuralCanvasProps) => {
  const opacity = rawOpacity * 0.55;
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return undefined;
    const ctx = cv.getContext('2d');
    if (!ctx) return undefined;
    let raf = 0;
    let W = 0;
    let H = 0;
    let running = false;
    let onScreen = true;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const pts: Particle[] = [];
    const size = () => {
      const r = cv.getBoundingClientRect();
      W = r.width;
      H = r.height;
      cv.width = W * dpr;
      cv.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size();
    for (let i = 0; i < density; i += 1) {
      pts.push({
        x: Math.random() * W,
        y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.26,
        vy: (Math.random() - 0.5) * 0.26,
        r: Math.random() * 1.7 + 0.8,
        a: Math.random() > 0.82,
      });
    }
    // One frame: advance positions + render. No rAF scheduling here.
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      pts.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;
      });
      for (let i = 0; i < pts.length; i += 1) {
        for (let j = i + 1; j < pts.length; j += 1) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const d = Math.hypot(dx, dy);
          if (d < 128) {
            ctx.strokeStyle = `rgba(${color},${(1 - d / 128) * 0.5 * opacity})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }
      pts.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, 7);
        if (p.a) {
          ctx.fillStyle = `rgba(${accent},${0.9 * opacity})`;
          ctx.shadowColor = `rgba(${accent},.9)`;
          ctx.shadowBlur = 8;
        } else {
          ctx.fillStyle = `rgba(${color},${0.85 * opacity})`;
          ctx.shadowBlur = 0;
        }
        ctx.fill();
        ctx.shadowBlur = 0;
      });
    };
    const loop = () => {
      draw();
      raf = requestAnimationFrame(loop);
    };
    // Run only while on-screen AND the tab is visible — protects INP/CPU/battery.
    const sync = () => {
      const shouldRun = onScreen && !document.hidden;
      if (shouldRun && !running) {
        running = true;
        raf = requestAnimationFrame(loop);
      } else if (!shouldRun && running) {
        running = false;
        cancelAnimationFrame(raf);
      }
    };
    draw(); // initial static paint so content is never blank
    const ro = new ResizeObserver(() => {
      size();
      draw();
    });
    ro.observe(cv);

    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !animate) {
      // Static by default, and always under reduced-motion: one frame, no loop.
      return () => ro.disconnect();
    }

    const io = new IntersectionObserver(
      (entries) => {
        onScreen = entries.some((e) => e.isIntersecting);
        sync();
      },
      { rootMargin: '120px' },
    );
    io.observe(cv);
    const onVisibility = () => sync();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      running = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [density, color, accent, opacity, animate]);
  return (
    <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
  );
};

/* ===== Reveal — CSS-only entrance. Resting state is VISIBLE (opacity:1);
   the rise is an auto-playing keyframe so content can never stay hidden. ===== */
interface RevealProps {
  children: ReactNode;
  delay?: number;
  y?: number;
  style?: CSSProperties;
}
export const Reveal = ({ children, delay = 0, y = 22, style = {} }: RevealProps) => (
  <div
    style={
      {
        ...style,
        '--fw-y': `${y}px`,
        animation: `fwRise .7s cubic-bezier(.2,.7,.2,1) ${delay}s both`,
      } as CSSProperties
    }
  >
    {children}
  </div>
);

/* ===== Buttons ===== */
interface BtnProps {
  children: ReactNode;
  kind?: 'primary' | 'ghost' | 'solid';
  onClick?: (e: ReactMouseEvent) => void;
  /** When set, renders a real anchor (crawlable) instead of a button. */
  href?: string;
  /** Button type when not a link — use 'submit' inside a <form>. */
  type?: 'button' | 'submit';
  dark?: boolean;
  small?: boolean;
}
export const Btn = ({
  children,
  kind = 'primary',
  onClick = () => {},
  href = undefined,
  type = 'button',
  dark = false,
  small = false,
}: BtnProps) => {
  const [h, setH] = useState(false);
  const base: CSSProperties = {
    fontFamily: 'var(--font-body)',
    fontWeight: 700,
    fontSize: small ? 14 : 15.5,
    padding: small ? '11px 20px' : '14px 26px',
    borderRadius: 'var(--r-pill)',
    border: 'none',
    cursor: 'pointer',
    transition: 'transform .18s, box-shadow .25s, background .2s',
    transform: h ? 'translateY(-2px)' : 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    whiteSpace: 'nowrap',
  };
  const styles: Record<string, CSSProperties> = {
    primary: {
      ...base,
      color: dark ? 'var(--indigo)' : '#fff',
      background: dark ? '#fff' : 'var(--indigo)',
      boxShadow: h ? '0 12px 28px -12px rgba(24,0,89,.55)' : '0 6px 16px -10px rgba(24,0,89,.4)',
    },
    ghost: {
      ...base,
      color: dark ? '#fff' : 'var(--indigo)',
      background: dark ? 'rgba(255,255,255,.06)' : 'transparent',
      border: dark ? '1px solid var(--glass-stroke)' : '1.5px solid var(--line)',
      boxShadow: h && !dark ? 'var(--shadow-sm)' : 'none',
    },
    solid: {
      ...base,
      color: '#fff',
      background: 'var(--indigo)',
      boxShadow: h ? '0 14px 30px -10px rgba(24,0,89,.5)' : 'var(--shadow-sm)',
    },
  };
  if (href) {
    return (
      <a
        href={href}
        style={{ ...styles[kind], textDecoration: 'none' }}
        onMouseEnter={() => setH(true)}
        onMouseLeave={() => setH(false)}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }
  return (
    <button
      type={type === 'submit' ? 'submit' : 'button'}
      style={styles[kind]}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

interface EyebrowProps {
  children: ReactNode;
  dark?: boolean;
  dot?: boolean;
}
export const Eyebrow = ({ children, dark = false, dot = true }: EyebrowProps) => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 9,
      padding: '7px 15px',
      borderRadius: 'var(--r-pill)',
      background: dark ? 'rgba(255,255,255,.05)' : 'var(--paper-cool)',
      border: dark ? '1px solid var(--glass-stroke)' : 'none',
      color: dark ? 'rgba(255,255,255,.85)' : 'var(--indigo)',
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: '.01em',
    }}
  >
    {dot && (
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: 'var(--green)',
          boxShadow: dark ? '0 0 9px var(--green)' : 'none',
        }}
      />
    )}
    {children}
  </div>
);

/* ===== Section wrappers ===== */
interface SectionProps {
  children: ReactNode;
  bg?: string;
  pad?: string;
  id?: string;
  style?: CSSProperties;
}
export const Section = ({
  children,
  bg = '#fff',
  pad = '110px 40px',
  id = undefined,
  style = {},
}: SectionProps) => (
  <section id={id} style={{ background: bg, padding: pad, position: 'relative', ...style }}>
    <div style={{ maxWidth: 1180, margin: '0 auto', position: 'relative' }}>{children}</div>
  </section>
);

interface SectionHeadProps {
  eyebrow: string;
  title: string;
  sub?: string;
  dark?: boolean;
  center?: boolean;
}
export const SectionHead = ({
  eyebrow,
  title,
  sub = '',
  dark = false,
  center = false,
}: SectionHeadProps) => (
  <div
    style={{
      maxWidth: center ? 720 : 640,
      margin: center ? '0 auto' : 0,
      textAlign: center ? 'center' : 'left',
      display: 'flex',
      flexDirection: 'column',
      alignItems: center ? 'center' : 'flex-start',
    }}
  >
    <Reveal>
      <Eyebrow dark={dark}>{eyebrow}</Eyebrow>
    </Reveal>
    <Reveal delay={0.05}>
      <h2
        className='fw-display'
        style={{
          fontSize: 42,
          lineHeight: 1.1,
          fontWeight: 800,
          color: dark ? '#fff' : 'var(--ink)',
          margin: '18px 0 0',
          letterSpacing: '-.03em',
        }}
      >
        {title}
      </h2>
    </Reveal>
    {sub && (
      <Reveal delay={0.1}>
        <p
          style={{
            fontSize: 18,
            lineHeight: 1.6,
            color: dark ? 'rgba(255,255,255,.7)' : 'var(--slate)',
            margin: '16px 0 0',
          }}
        >
          {sub}
        </p>
      </Reveal>
    )}
  </div>
);

/* Shared LineIcon re-export for convenience in section files. */
export { LineIcon };
